import { passengerData, config } from '../data/flight-67/passengers.js'
import { passengerDataEs419 } from '../data/flight-67/passengers.es-419.js'
import { passengerDataTrTR } from '../data/flight-67/passengers.tr-TR.js'
import {
	DEFAULT_LOCALE,
	SPANISH_LOCALE,
	TURKISH_LOCALE,
	SUPPORTED_LOCALES,
	translations,
} from '../data/flight-67/translations.js'

const PLATFORM_NAMES = {
	insta: 'Instagram',
	tiktok: 'TikTok',
	snap: 'Snapchat',
	youtube: 'YouTube',
}

function consistentConfidence(identifier) {
	let hash = 0
	for (let i = 0; i < identifier.length; i++) {
		hash = (hash << 5) - hash + identifier.charCodeAt(i)
		hash = hash & hash
	}
	const normalized = Math.abs(hash % 1400) / 100
	return (85 + normalized).toFixed(1)
}

function normalizeLocale(locale) {
	return SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE
}

function storedLocale() {
	try {
		return normalizeLocale(window.localStorage.getItem('flight67.locale'))
	} catch {
		return DEFAULT_LOCALE
	}
}

function localizedPassengerData(locale) {
	const localeOverlays = {
		[SPANISH_LOCALE]: passengerDataEs419,
		[TURKISH_LOCALE]: passengerDataTrTR,
	}

	return Object.fromEntries(
		Object.entries(passengerData).map(([seatId, passenger]) => {
			const overlay = localeOverlays[locale]?.[seatId] || {}
			const purchases = passenger.purchases?.map((purchase, index) => ({
				...purchase,
				...(overlay.purchases?.[index] || {}),
			}))

			return [
				seatId,
				{
					...passenger,
					id: seatId,
					confidenceSeed: passenger.name,
					...overlay,
					...(purchases ? { purchases } : {}),
				},
			]
		}),
	)
}

export default function flight67() {
	const initialLocale = storedLocale()
	const defaultDocumentTitle = document.title

	return {
		screen: 'intro',
		locale: initialLocale,
		selectedSeat: null,
		passengers: localizedPassengerData(initialLocale),
		config,
		cells: [],
		toggles: {
			biometrics: true,
			digital: false,
			websites: false,
			texts: false,
			purchases: false,
			facial: false,
			suspect: false,
		},

		init() {
			this.cells = this.buildCells()
			this.setLocale(this.locale)
		},

		buildCells() {
			const cells = []

			for (let row = 1; row <= config.rows; row++) {
				for (let col = 0; col < config.cols; col++) {
					const id = `${row}${config.letters[col]}`
					cells.push({ type: 'seat', id, key: id })

					if (col === 1) {
						cells.push({ type: 'aisle', key: `aisle-${row}` })
					}
				}
			}

			return cells
		},

		get alex() {
			return this.passengers[this.config.alexSeat]
		},

		get selected() {
			return this.selectedSeat ? this.passengers[this.selectedSeat] : null
		},

		start() {
			this.screen = 'play'
		},

		backToIntro() {
			this.screen = 'intro'
			this.selectedSeat = null
			this.toggles = {
				biometrics: true,
				digital: false,
				websites: false,
				texts: false,
				purchases: false,
				facial: false,
				suspect: false,
			}
		},

		imageUrl(passenger) {
			return passenger?.image ? `/images/flight-67/passengers/${passenger.image}` : ''
		},

		t(key, replacements = {}) {
			const dictionary = translations[this.locale] || translations[DEFAULT_LOCALE]
			let value = dictionary[key] ?? translations[DEFAULT_LOCALE][key] ?? key

			Object.entries(replacements).forEach(([name, replacement]) => {
				value = value.replaceAll(`{${name}}`, replacement)
			})

			return value
		},

		setLocale(locale) {
			this.locale = normalizeLocale(locale)
			this.passengers = localizedPassengerData(this.locale)

			try {
				window.localStorage.setItem('flight67.locale', this.locale)
			} catch {
				// The activity still works when browser storage is unavailable.
			}

			if (this.locale !== DEFAULT_LOCALE) {
				const localizedTitle = defaultDocumentTitle.replace('Flight 67', this.t('metaTitle'))
				document.title = localizedTitle === defaultDocumentTitle ? this.t('metaTitle') : localizedTitle
			} else {
				document.title = defaultDocumentTitle || this.t('metaTitle')
			}
			const description = document.querySelector('meta[name="description"]')
			if (description) description.setAttribute('content', this.t('metaDescription'))
		},

		platformName(platform) {
			return PLATFORM_NAMES[platform] || platform
		},

		sexLabel(sex) {
			return sex === 'M' ? this.t('male') : this.t('female')
		},

		formatDate(date) {
			if (!date) return ''
			return new Intl.DateTimeFormat(this.locale, {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
				timeZone: 'UTC',
			}).format(new Date(`${date}T00:00:00Z`))
		},

		suspectScore(passenger) {
			if (!passenger) return 0

			let score = passenger.baseScore

			if (this.toggles.purchases) score += passenger.purchaseScore
			if (this.toggles.texts) score += passenger.textScore
			if (this.toggles.facial) score += passenger.facialScore
			if (this.toggles.digital) score += Math.floor(passenger.baseScore * 0.2)
			if (this.toggles.websites) score += Math.floor(passenger.baseScore * 0.15)

			let percentage = Math.min(97, Math.floor((score / 160) * 100))

			if (passenger.isAlex) {
				if (this.toggles.facial) {
					if (this.toggles.purchases && this.toggles.texts) {
						percentage = 99
					} else if (this.toggles.texts) {
						percentage = Math.max(percentage, 94)
					} else {
						percentage = Math.max(percentage, 85)
					}
				}
			}

			if (passenger.isCulprit) {
				if (this.toggles.purchases && !this.toggles.facial) {
					percentage = Math.max(percentage, 94)
				}
				if (this.toggles.purchases && this.toggles.texts && !this.toggles.facial) {
					percentage = Math.max(percentage, 92)
				}
				if (this.toggles.purchases && this.toggles.texts && this.toggles.facial) {
					percentage = 98
				}
			}

			return percentage
		},

		facialConfidence(passenger) {
			return passenger ? consistentConfidence(passenger.confidenceSeed) : '0.0'
		},

		isHighRisk(passenger) {
			return this.suspectScore(passenger) >= 80
		},

		toggle(name) {
			this.toggles[name] = !this.toggles[name]
		},

		selectSeat(seatId) {
			if (!seatId || seatId === this.config.youSeat) return
			this.selectedSeat = this.selectedSeat === seatId ? null : seatId
		},

		clearSelection() {
			this.selectedSeat = null
		},

		handlePlaneClick(event) {
			if (!event.target.closest('.flight-67-seat')) {
				this.clearSelection()
			}
		},

		isYou(seatId) {
			return seatId === this.config.youSeat
		},

		isAlexSeat(seatId) {
			return seatId === this.config.alexSeat
		},

		seatAriaLabel(seatId) {
			if (this.isYou(seatId)) return this.t('yourSeatAria')
			if (this.isAlexSeat(seatId)) return this.t('alexSeatAria', { seat: seatId })
			return this.t('seatAria', { seat: seatId })
		},

		seatLabel(seatId) {
			if (this.isYou(seatId)) return this.t('you')
			return seatId
		},
	}
}
